import React, { useEffect, useState, useCallback } from 'react';
import { useToast } from 'src/context/ToastProvider';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Container from 'src/components/shared/Container';
import { getUserPreferenceUIConfig, stringAvatar } from 'shared-utils';
import { generateCompanyLogoAvatarSrc } from 'src/utils/companyLogo';
import {
	GaugingCandidateStatus,
	SkillDocument,
	TechStackType,
	UIAssessmentSkillWeightageConfig,
	UIHiringAssessment,
	UserPreferenceType,
	WorkingMode,
} from 'types';
import { useGetHiringAssessment, useGetHiringAssessmentSkills } from 'src/hooks/hiringAssessment.hooks';
import { useUserContext } from 'src/context/UserProvider';

import { Avatar } from '@mui/material';
import PreferenceWrapper from 'src/components/dashboard/dashboardTopSection/scheduleInterviewDrawer/leftSection/PreferenceWrapper';
import PreferenceTab from 'src/components/dashboard/dashboardTopSection/scheduleInterviewDrawer/PreferenceTab';
import { Button } from 'flowbite-react';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa6';
import { useMutation } from 'react-query';
import { createHiringAssessment } from 'src/apis/hiringAssessment';
import { BsArrowLeft } from 'react-icons/bs';

const CreateAssessmentWithParameterAndWeightagePage = () => {
	const [searchParams] = useSearchParams();
	const { user } = useUserContext();
	const navigate = useNavigate();
	const toast = useToast();
	const [hiringAssessment, setHiringAssessment] = useState<UIHiringAssessment>();
	const [avatarImg, setAvatarImg] = useState('');
	const [userPreference, setUserPreference] = React.useState<UserPreferenceType>();
	const gaugingCandidateStatus = Object.values(GaugingCandidateStatus);
	const [workingMode, setWorkingMode] = useState<WorkingMode | null>(null);
	const [salaryRange, setSalaryRange] = useState({ min: 0, max: 0 });
	const [assessmentDeadline, setAssessmentDeadline] = useState('');
	const { benchmarkingFormData, setBenchmarkingFormData } = useUserContext();

	const [skills, setSkills] = useState<UIAssessmentSkillWeightageConfig[]>([]);
	const getHiringAssessmentSkills = useGetHiringAssessmentSkills(benchmarkingFormData);
	useEffect(() => {
		if (getHiringAssessmentSkills?.data?.skills) {
			setSkills(getHiringAssessmentSkills?.data?.skills);
		}
	}, [getHiringAssessmentSkills?.data?.skills]);

	const createHiringAssessmentMutation = useMutation({
		mutationFn: createHiringAssessment,
		onSuccess: data => {
			const hiringAssessment = data.hiringAssessment as UIHiringAssessment;
			if (!hiringAssessment?._id) {
				toast({
					title: 'Assessment creation failed, please contact support: support@levelup.inc',
					type: 'error',
				});
				return;
			}
			navigate(`/benchmarking/share-hiring-assessment?hiringAssessmentId=${hiringAssessment?._id}`);

			toast({
				title: 'Assessment created successfully!',
				type: 'success',
			});
		},
		onError: () => {
			toast({
				title: 'Assessment creation failed, please contact support: support@levelup.inc',
				type: 'error',
			});
		},
	});

	const generateAvatar = useCallback(async () => {
		if (user?.registeredCompany?.logo) {
			const res = await generateCompanyLogoAvatarSrc(
				user?.registeredCompany?.logo ?? '',
				user?.registeredCompany?.url ?? '',
			);

			setAvatarImg(res);
		} else if (user?.registeredCompany) {
			const res = await generateCompanyLogoAvatarSrc(
				user?.registeredCompany?.logo ?? '',
				user?.registeredCompany?.url ?? '',
			);

			setAvatarImg(res);
		}
	}, [user?.registeredCompany?.logo]);

	const { data: hiringAssessmentData, isLoading: hiringAssessmentLoading } = useGetHiringAssessment({
		hiringAssessmentId: searchParams.get('hiringAssessmentId') || '',
		token: localStorage.getItem('guestUserJwtToken') || '',
	});

	useEffect(() => {
		if (hiringAssessmentData) {
			setHiringAssessment(hiringAssessmentData?.hiringAssessment);
		}
	}, [hiringAssessmentData]);

	useEffect(() => {
		generateAvatar();
	}, [user?.registeredCompany?.logo, user?.registeredCompany?.url]);

	useEffect(() => {
		if (!benchmarkingFormData || !benchmarkingFormData.userPreferenceSnapshot) {
			navigate('/benchmarking/create-hiring-assessment');
		} else {
			setUserPreference(benchmarkingFormData.userPreferenceSnapshot);
			setSalaryRange({
				min: benchmarkingFormData.salaryRange?.min ?? 0,
				max: benchmarkingFormData.salaryRange?.max ?? 0,
			});
			setAssessmentDeadline(benchmarkingFormData.assessmentDeadline?.toString() || '');
			setWorkingMode(benchmarkingFormData.workingMode ?? null);
		}
	}, []);
	const handlePreviousPage = () => {
		navigate('/benchmarking/create-hiring-assessment');
	};
	const handleWeightageChange = (index: number, value: number) => {
		const updatedSkills = [...skills];
		updatedSkills[index].weightage = value;
		const totalWeightage = updatedSkills.reduce((sum, skill) => sum + skill.weightage, 0);

		if (totalWeightage > 100) {
			toast({
				title: 'Total weightage of skills should not exceed 100%',
				type: 'error',
			});
		} else {
			setSkills(updatedSkills);
		}
	};
	return (
		<Container>
			<div className="flex items-center justify-center max-w-[580px] mx-auto my-4">
				<div className="flex flex-col">
					<div className="bg-light-4 rounded-lg p-6 bg-white">
						<div className="flex flex-col items-start gap-4 rounded-lg mt-2">
							<div className="flex items-center gap-4 rounded-lg w-full">
								<Avatar
									alt="companyLogo"
									src={avatarImg}
									sx={{
										width: '51.2px',
										height: '51.2px',
										minWidth: '51.2px',
										minHeight: '51.2px',
										color: 'white',
										border: '1px solid #E5E5E5',
									}}
								>
									{stringAvatar(
										user?.registeredCompany?.name.trim().toUpperCase() || user?.registeredCompany?.name || '',
									)}
								</Avatar>

								<div className="flex pr-2 flex-col items-start gap-1 w-full">
									<div className="text-[20px] font-[700] leading-[120%] text-dark-0 font-inter text-[#28293D]">
										{user?.registeredCompany?.name}
									</div>
									<div className="flex items-center text-center gap-1">
										<div className="text-[14px] font-normal leading-[20px] text-[#555770] font-inter">
											{getUserPreferenceUIConfig({ text: userPreference?.developmentRole || '' })?.finalText}
										</div>
									</div>
								</div>
							</div>
							<div className="flex items-start content-start gap-2 flex-wrap">
								<div className="flex gap-[4px]">
									<img src="/assets/recruiterImages/briefcase.svg" alt="Briefcase" className="max-w-4 w-full" />
									<span className="text-[#28293D] font-inter text-[12px] font-normal leading-[16px]">
										{getUserPreferenceUIConfig({ text: userPreference?.experienceLevel || '' })?.finalText}
									</span>
								</div>

								{!hiringAssessment?.userPreferenceSnapshot?.techStack?.includes(TechStackType.OTHER) ? (
									<>
										<span className="text-[#28293D] font-inter text-[12px] font-medium leading-[16px]">|</span>
										<div className="flex gap-[4px]">
											<img src="/assets/recruiterImages/tag.svg" alt="Tag" className="max-w-4 w-full" />
											<span className="text-[#28293D] font-inter text-[12px] font-medium leading-[16px]">
												{getUserPreferenceUIConfig({ text: userPreference?.techStack || '' })?.finalText}
											</span>
										</div>
									</>
								) : null}

								<div className="flex gap-[4px]">
									<span className="text-[#28293D] font-inter text-[12px] font-bold leading-[16px]">•</span>
									<span className="text-[#28293D] font-inter text-[12px] font-normal leading-[16px]">
										{salaryRange?.min} - {salaryRange?.max} LPA
									</span>
								</div>
							</div>
						</div>
						<div className="flex flex-col pt-[24px]">
							<div className="text-[#28293D] text-start font-inter text-[20px] font-[700] leading-[24px]">
								Modify Parameters & Weightage
							</div>
							<div className="text-[#28293D] text-start font-inter text-[14px] font-[400] leading-[20px] pt-[11px]">
								Our adaptive engine has selected key evaluation aspects based on the job description. Adjust the
								parameters and weightage as needed.
							</div>
							<div>
								<div className="text-[#28293D] text-start font-inter text-[14px] font-[600] leading-[20px] pt-[12px]">
									Evaluation Parameters:
								</div>
								<div className="grid md:grid-cols-2 grid-cols-1 gap-y-4 mx-auto justify-between gap-14 pt-[12px]">
									{skills.map((skill, index) => {
										return (
											<div key={index} className="flex items-center justify-between md:gap-4">
												<span className="w-1/2 text-center md:font-medium">{String(skill?.skill)}</span>
												<div className="relative md:w-[80px] w-[100px]">
													<input
														className="w-full rounded-lg border-gray-300 bg-white py-2.5 pl-2 pr-6 text-sm font-light tracking-wide placeholder-gray-400 transition-all duration-300 focus:border-blue-500 focus:ring focus:ring-blue-500/20 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:bg-slate-800 dark:text-white/80 appearance-none"
														type="number"
														defaultValue={skill?.weightage}
														min="0"
														onChange={e => handleWeightageChange(index, Math.max(0, Number(e.target.value)))}
														style={{
															MozAppearance: 'textfield',
															WebkitAppearance: 'none',
															appearance: 'none',
														}}
														onWheel={e => e.currentTarget.blur()}
													/>
													<span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
												</div>
											</div>
										);
									})}
								</div>
							</div>
						</div>
						{/* <div className="pt-[30px]">
							<PreferenceWrapper>
								<div className="flex items-center mb-4">
									<span className="font-inter text-sm not-italic font-[500] leading-[20px]">
										Gauging candidate based on{' '}
									</span>
								</div>
								<div className="font-inter text-sm not-italic font-medium leading-5 flex flex-wrap gap-2">
									{gaugingCandidateStatus.map(candidate => (
										<PreferenceTab
											key={candidate}
											text={candidate.toLowerCase().replace(/\s/g, '')}
											isSelected={candidate === userPreference?.gaugingCandidateStatus}
											onSelect={() => {
												setUserPreference(prevState => ({
													...prevState,
													gaugingCandidateStatus: candidate as GaugingCandidateStatus,
												}));
											}}
										/>
									))}
								</div>
							</PreferenceWrapper>
						</div> */}

						{/* <div className="pt-[32px] font-inter text-[14px] font-[400] leading-[20px] pb-[24px]">
							Check out some of the sample{' '}
							<span className="text-[#7777E7] font-inter text-[14px] font-[600] leading-[20px]">
								Questions &gt;&gt;
							</span>
						</div> */}
					</div>
					<div
						className="bg-white dark:bg-[#111E2C] sticky flex items-center gap-5 bottom-[55px] md:bottom-0 px-[16px] md:px-[24px] rounded-b-[12px]"
						style={{
							boxShadow: 'rgba(0, 0, 0, 0.05) 0px -2px 4px 0px',
						}}
					>
						<button
							className="flex px-[8px] py-[12px] justify-center items-center gap-[8px] rounded-[6px] bg-[#EBEBF0] border border-solid border-[#C7C9D9]"
							onClick={handlePreviousPage}
						>
							<BsArrowLeft className="mr-1 text-[#8F90A6] font-inter font-[14px] leading-[21px]" />
						</button>
						<Button
							type="button"
							color="purplePlaced"
							className="my-3 w-full"
							onClick={() => {
								const totalWeightage = skills.reduce((sum, skill) => sum + skill.weightage, 0);

								if (totalWeightage > 100) {
									toast({
										title: 'Total weightage of skills should not exceed 100%',
										type: 'error',
									});
									return;
								}
								const finalUserPreference = {
									experienceLevel: userPreference?.experienceLevel,
									careerPath: userPreference?.careerPath,
									developmentRole: userPreference?.developmentRole,
									techStack: userPreference?.techStack,
								};

								const transformedSkills = skills.map(skill => {
									return {
										_id: skill.skillId || '',
										weightage: skill.weightage,
										createdAt: new Date(),
										updatedAt: new Date(),
										skill: {
											_id: skill.skillId || '',
											name: skill.skill || '',
										} as unknown as SkillDocument,
									};
								});

								const requestData = {
									userPreferenceSnapshot: finalUserPreference,
									assessmentDeadline: new Date(assessmentDeadline),
									salaryRange: salaryRange,
									skills: transformedSkills,
									workingMode: workingMode ?? undefined,
									gaugingCandidateStatus: userPreference?.gaugingCandidateStatus,
								};
								if (salaryRange) {
									createHiringAssessmentMutation.mutate(requestData);
									setBenchmarkingFormData({
										userPreferenceSnapshot: {} as UserPreferenceType,
										salaryRange: { min: 0, max: 0 },
										assessmentDeadline: undefined,
										workingMode: undefined,
									});
								} else {
									toast({
										title: 'Please select a salaryRange',
										type: 'error',
									});
								}
							}}
						>
							{'Create Assessment'}
						</Button>
					</div>
				</div>
			</div>
		</Container>
	);
};

export default CreateAssessmentWithParameterAndWeightagePage;
